/**
 * Makes a scrollable element follow a pointer dragged across it.
 *
 * Put the returned ref on the element that scrolls — the one with
 * `overflow: auto` — not on a wrapper. It reads like a normal ref
 * (`ref.current?.scrollBy(...)`) and can be passed straight to `ref=`.
 *
 * Why this exists at all is in `lib/drag-scroll.ts`: the till reports a finger
 * as a mouse, so taps work and drags do nothing. Real touch is left to the
 * browser, which is already scrolling it.
 *
 * The press must survive. A dish card carries buttons, and a drag that begins
 * on one of them must scroll without firing it: nothing moves until the pointer
 * has travelled past the threshold, and once it has, the click that the browser
 * sends afterwards is swallowed on its way down.
 *
 * It is a CALLBACK ref, not a plain `useRef`, and that is not decoration. Both
 * boards return early while they are loading and while they have nothing to
 * show, so the scrolling element does not exist on the first render. A plain
 * ref would have been read as null once, and the listeners would never have
 * been attached when the element finally appeared — which is exactly what
 * happened, and was found by dragging the real board rather than by reading
 * this file.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { exceedsThreshold, nextOffset, startsDrag } from "../lib/drag-scroll";

/** A ref that can be passed to `ref=` and still read through `.current`. */
export interface DragScrollRef<T> {
  (node: T | null): void;
  current: T | null;
}

export function useDragScroll<T extends HTMLElement>(
  axis: "x" | "y" = "y",
): DragScrollRef<T> {
  // The element, as state: the effect must re-run when it appears, not only
  // when the component first mounts.
  const [node, setNode] = useState<T | null>(null);
  const held = useRef<DragScrollRef<T> | null>(null);

  const attach = useCallback((next: T | null) => {
    if (held.current) held.current.current = next;
    setNode(next);
  }, []);

  if (!held.current) {
    const ref = attach as DragScrollRef<T>;
    ref.current = null;
    held.current = ref;
  }

  useEffect(() => {
    const el = node;
    if (!el) return;

    const horizontal = axis === "x";
    let pointerId: number | null = null;
    let startPos = 0;
    let startOther = 0;
    let startOffset = 0;
    let dragging = false;
    // Survives pointerup so the click that follows a drag can be swallowed.
    let swallowNextClick = false;

    const pos = (e: PointerEvent) => (horizontal ? e.clientX : e.clientY);
    const other = (e: PointerEvent) => (horizontal ? e.clientY : e.clientX);

    const onPointerDown = (e: PointerEvent) => {
      swallowNextClick = false;
      if (!startsDrag(e.pointerType, e.button)) return;
      pointerId = e.pointerId;
      startPos = pos(e);
      startOther = other(e);
      startOffset = horizontal ? el.scrollLeft : el.scrollTop;
      dragging = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      if (!dragging) {
        if (!exceedsThreshold(pos(e) - startPos, other(e) - startOther)) return;
        dragging = true;
        swallowNextClick = true;
        // Take the pointer so the drag survives leaving the surface, and stop
        // the browser turning it into a text selection.
        el.setPointerCapture(e.pointerId);
      }

      e.preventDefault();
      const max = horizontal
        ? el.scrollWidth - el.clientWidth
        : el.scrollHeight - el.clientHeight;
      const offset = nextOffset(startOffset, startPos, pos(e), max);
      if (horizontal) el.scrollLeft = offset;
      else el.scrollTop = offset;
    };

    const end = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      pointerId = null;
      dragging = false;
    };

    // Capture phase: the click is stopped before it reaches the dish button.
    const onClick = (e: MouseEvent) => {
      if (!swallowNextClick) return;
      swallowNextClick = false;
      e.preventDefault();
      e.stopPropagation();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
    el.addEventListener("click", onClick, true);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", end);
      el.removeEventListener("pointercancel", end);
      el.removeEventListener("click", onClick, true);
    };
  }, [node, axis]);

  return held.current;
}
